import isNil from 'lodash/isNil';
import { useEffect, useMemo } from 'react';
import { Actor, StateFrom } from 'xstate';
import { useSearchActionPlan } from '@/components/global-search/hooks/useSearchActionPlan';
import { useSearchBuilding } from '@/components/global-search/hooks/useSearchBuilding';
import { useSearchBuildingId } from '@/components/global-search/hooks/useSearchBuildingId';
import { useSearchEconomicUnit } from '@/components/global-search/hooks/useSearchEconomicUnit';
import { useSearchNavigationItems } from '@/components/global-search/hooks/useSearchNavigationItems';
import { useSearchPortfolio } from '@/components/global-search/hooks/useSearchPortfolio';
import { useSearchScenario } from '@/components/global-search/hooks/useSearchScenario';
import { globalSearchMachine } from '@/components/global-search/machines/globalSearchMachine';
import { applyFilter, sortItemsByType } from '@/components/global-search/utils';

export const useGlobalSearchQueries = ({
  state,
  send,
}: {
  state: StateFrom<typeof globalSearchMachine>;
  send: Actor<typeof globalSearchMachine>['send'];
}) => {
  const query = state.context.query;

  const { buildings, loading: isBuildingsLoading, error: errorBuildingsLoading } = useSearchBuilding(query);
  const { buildingIds, loading: isBuildingIdsLoading, error: errorBuildingIdsLoading } = useSearchBuildingId(query);
  const { actionPlans, loading: isActionPlansLoading, error: errorActionPlansLoading } = useSearchActionPlan(query);
  const { scenarios, loading: isScenariosLoading, error: errorScenariosLoading } = useSearchScenario(query);
  const { portfolios, loading: isPortfoliosLoading, error: errorPortfoliosLoading } = useSearchPortfolio(query);
  const {
    economicUnits,
    loading: isEconomicUnitsLoading,
    error: errorEconomicUnitsLoading,
  } = useSearchEconomicUnit(query);
  const navigationItems = useSearchNavigationItems();

  useEffect(() => {
    const filtered = applyFilter({ inputData: [...navigationItems], query });
    // Sort items by type to maintain grouping for keyboard navigation
    const results = sortItemsByType(filtered);
    send({ type: 'SYSTEM_SET_NAVIGATION_RESULTS', results });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationItems]);

  const isLoading = useMemo(
    () =>
      isBuildingsLoading ||
      isBuildingIdsLoading ||
      isActionPlansLoading ||
      isScenariosLoading ||
      isPortfoliosLoading ||
      isEconomicUnitsLoading,
    [
      isActionPlansLoading,
      isBuildingsLoading,
      isBuildingIdsLoading,
      isEconomicUnitsLoading,
      isPortfoliosLoading,
      isScenariosLoading,
    ],
  );

  const errors = useMemo(
    () => [
      errorBuildingsLoading,
      errorBuildingIdsLoading,
      errorActionPlansLoading,
      errorScenariosLoading,
      errorPortfoliosLoading,
      errorEconomicUnitsLoading,
    ],
    [
      errorActionPlansLoading,
      errorBuildingsLoading,
      errorBuildingIdsLoading,
      errorEconomicUnitsLoading,
      errorPortfoliosLoading,
      errorScenariosLoading,
    ],
  );

  const allItems = useMemo(
    () => [
      ...navigationItems,
      ...buildings,
      ...buildingIds,
      ...actionPlans,
      ...scenarios,
      ...portfolios,
      ...economicUnits,
    ],
    [actionPlans, buildings, buildingIds, economicUnits, navigationItems, portfolios, scenarios],
  );

  useEffect(() => {
    if (errors.some((it) => !isNil(it))) {
      send({ type: 'SYSTEM_FETCH_ITEMS_ERROR' });
      return;
    }

    if (!isLoading) {
      const filtered = applyFilter({ inputData: allItems, query });
      // Sort items by type to maintain grouping for keyboard navigation
      const results = sortItemsByType(filtered);
      send({ type: 'SYSTEM_FETCH_ITEMS_SUCCESS', results });
    }
  }, [allItems, errors, isLoading, query, send]);
};
